import { useState } from "react";
import { timeAgo } from "../../shared/format.js";
import { Spinner } from "../../shared/ui/index.js";
import { useEventSocket, useFeed, useLike, type FeedItemDto } from "./feed.api.js";

export function WallFeed({
  eventId,
  token,
  email,
  readOnly = false,
  embedded = false,
}: {
  eventId: string;
  token: string | undefined;
  email: string;
  /** Vue organisateur : fil consultable, sans bouton J'aime actif. */
  readOnly?: boolean;
  /** Rendu dans un conteneur qui gère déjà le scroll (ex. écran organisateur). */
  embedded?: boolean;
}) {
  const q = useFeed(eventId, token, email);
  const like = useLike(eventId, token, email);
  const live = useEventSocket(eventId, token);

  const items = q.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div className={embedded ? "" : "sv-scroll h-full px-4 py-4"}>
      <div className="mx-auto flex max-w-lg flex-col gap-4">
        <p className="flex items-center gap-1.5 text-xs" style={{ color: "var(--theme-muted)" }}>
          <span
            className={`inline-block h-2 w-2 rounded-full ${live ? "bg-emerald-400" : "bg-stone-300"}`}
          />
          {live ? "En direct" : "Actualisation auto"}
        </p>

        {q.isLoading && <Spinner />}
        {!q.isLoading && items.length === 0 && (
          <p className="py-10 text-center text-sm" style={{ color: "var(--theme-muted)" }}>
            Aucune publication pour l'instant. Soyez le premier !
          </p>
        )}

        {items.map((it) => (
          <Post
            key={it.id}
            item={it}
            readOnly={readOnly}
            onLike={() => email && like.mutate({ id: it.id, like: !it.likedByMe })}
          />
        ))}

        {q.hasNextPage && (
          <button
            onClick={() => q.fetchNextPage()}
            disabled={q.isFetchingNextPage}
            className="mx-auto rounded-full px-4 py-2 text-sm"
            style={{ background: "var(--theme-accent-soft)", color: "var(--theme-text)" }}
          >
            {q.isFetchingNextPage ? "…" : "Voir plus"}
          </button>
        )}
      </div>
    </div>
  );
}

function Post({
  item,
  onLike,
  readOnly,
}: {
  item: FeedItemDto;
  onLike: () => void;
  readOnly: boolean;
}) {
  const [zoom, setZoom] = useState(false);
  const rend = (...k: string[]) =>
    k.map((x) => item.renditions.find((r) => r.kind === x)?.url).find(Boolean);
  const img = rend("full", "poster", "thumb");
  const mp4 = rend("mp4");

  return (
    <article
      className="overflow-hidden rounded-2xl border shadow-sm"
      style={{ background: "var(--theme-surface)", borderColor: "var(--theme-border)" }}
    >
      <header className="flex items-center gap-2 px-3 py-2.5">
        <span
          className="flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold"
          style={{ background: "var(--theme-accent-soft)", color: "var(--theme-text)" }}
        >
          {(item.pseudo[0] ?? "?").toUpperCase()}
        </span>
        <div className="min-w-0">
          <p className="truncate text-sm font-medium" style={{ color: "var(--theme-text)" }}>
            {item.pseudo}
          </p>
          <p className="text-[11px]" style={{ color: "var(--theme-muted)" }}>
            {timeAgo(item.createdAt)}
          </p>
        </div>
      </header>

      {item.kind === "video" && mp4 ? (
        <video src={mp4} controls playsInline poster={img} className="w-full bg-black" />
      ) : (
        <button onClick={() => setZoom(true)} className="block w-full">
          <img src={img} alt={item.note ?? ""} className="max-h-[70vh] w-full object-cover" />
        </button>
      )}

      {item.note && (
        <p className="px-3 pt-2 text-sm" style={{ color: "var(--theme-text)" }}>
          {item.note}
        </p>
      )}

      <footer className="flex items-center gap-2 px-3 py-2.5">
        {readOnly ? (
          <span
            className="flex items-center gap-1.5 px-1 py-1.5 text-sm"
            style={{ color: "var(--theme-muted)" }}
          >
            <span>❤️</span>
            <span>{item.likeCount} j'aime</span>
          </span>
        ) : (
          <button
            onClick={onLike}
            className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm transition"
            style={{
              background: item.likedByMe ? "var(--theme-accent-soft)" : "transparent",
              color: item.likedByMe ? "var(--theme-primary)" : "var(--theme-muted)",
            }}
          >
            <span className={item.likedByMe ? "scale-110" : ""}>
              {item.likedByMe ? "❤️" : "🤍"}
            </span>
            {item.likeCount > 0 && <span>{item.likeCount}</span>}
            <span className="hidden sm:inline">J'aime</span>
          </button>
        )}
      </footer>

      {zoom && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-3"
          onClick={() => setZoom(false)}
        >
          <img src={rend("full", "mp4")} alt="" className="max-h-full max-w-full rounded" />
        </div>
      )}
    </article>
  );
}
